import { useCallback, useEffect, useRef, useState } from "react";
import { ContentType, tmdb, toCards, yearOf } from "../api/tmdb";
import { buildHostedTree, episodeLabel, episodeSlots, parseHostedKey, seasonNumbers } from "../../hosted-folders.js";
import { TMDB_LANGUAGE, languageName } from "../../intake/languages.mjs";

// Admin console: map torrents to titles. Set POKU_ADMIN_TOKEN in web/.env
// (server-side only) and paste the same token here; it is sent as a header
// and never stored anywhere but this browser tab's memory.
//
// TorBox (optional, recommended): set POKU_TORBOX_KEY in web/.env and mapped
// titles stream from TorBox's cloud cache — instant for cached torrents,
// cloud-downloaded otherwise — so nothing is stored on the host machine.
//
// Find titles by name (poster+year results from TMDB) or paste a raw TMDB id
// to fetch that title for confirmation. After a mapping's torrent finishes
// downloading, the server remuxes it to HLS in the background; the entry's
// audio tracks can then be renamed (e.g. "Hindi", "Tamil") and one marked as
// the default language — master-playlist edits only, no re-encode.

const SEARCH_DEBOUNCE_MS = 350;

export default function AdminScreen() {
  const [token, setToken] = useState("");
  const [authed, setAuthed] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [entries, setEntries] = useState([]);

  const headers = useCallback(() => ({ "content-type": "application/json", "x-admin-token": token }), [token]);

  const load = useCallback(async () => {
    try {
      const r = await fetch("/api/torrent/list", { headers: headers() });
      if (r.status === 401) {
        setAuthed(false);
        setAuthError("Wrong token (or POKU_ADMIN_TOKEN is not set on the server).");
        return;
      }
      const d = await r.json();
      setEntries(d.entries || []);
      setAuthed(true);
      setAuthError(null);
    } catch {
      setAuthError("Could not reach the server.");
    }
  }, [headers]);

  useEffect(() => {
    if (authed) load();
  }, [authed, load]);

  if (!authed) {
    return (
      <main className="page admin-page">
        <h1>PoKu Admin</h1>
        <p className="muted">Enter the admin token (POKU_ADMIN_TOKEN set on the server) to manage torrent mappings.</p>
        <form
          className="admin-login"
          onSubmit={(e) => {
            e.preventDefault();
            setAuthError(null);
            setAuthed(true); // trigger load(); load() flips back on 401
            load();
          }}
        >
          <input
            type="password"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="Admin token"
            autoFocus
          />
          <button className="btn btn-primary" type="submit">
            Unlock
          </button>
        </form>
        {authError && <p className="source-check bad">{authError}</p>}
      </main>
    );
  }

  return (
    <main className="page admin-page">
      <h1>PoKu Admin — Torrent mappings</h1>
      <p className="muted">
        Find a title by name (or paste its TMDB id), add the magnet, and playback streams from the torrent's swarm
        first. Once downloaded, dual-audio files are remuxed to HLS with a language switcher; if the swarm is dead it
        falls back to the usual provider.
      </p>
      <TorBoxCard />
      <LiveTvChannelsCard headers={headers} />
      <YouTubeCard headers={headers} />
      <HybridCard headers={headers} />
      <HostedFileCard headers={headers} />
      <TrackExtractorCard headers={headers} />
      <SubtitlesCard headers={headers} />
      <MappingForm headers={headers} onAdded={load} />
      <MappingList entries={entries} headers={headers} onChanged={load} />
    </main>
  );
}

// TorBox cloud-streaming status card. Read-only: the key itself lives in
// web/.env on the server (POKU_TORBOX_KEY); this just surfaces its state.
function TorBoxCard() {
  const [state, setState] = useState(null);
  useEffect(() => {
    fetch("/api/torbox/status?enabled=1")
      .then((r) => r.json())
      .then((d) => setState(d.enabled === true || d.enabled === false ? d : { enabled: false }))
      .catch(() => setState({ enabled: false }));
  }, []);
  if (!state) return null;
  return (
    <div className={`admin-torbox${state.enabled ? " on" : ""}`}>
      <strong>☁️ TorBox cloud streaming: {state.enabled ? "ON" : "OFF"}</strong>
      <span className="muted small">
        {state.enabled
          ? "Mapped titles stream from TorBox's cloud cache — nothing downloads on this machine. Set POKU_TORBOX_KEY in web/.env to change the key."
          : "Add POKU_TORBOX_KEY=<your TorBox API key> to web/.env (torbox.app → Settings → API) and restart the server to stream mapped titles from the cloud instead of downloading locally."}
      </span>
    </div>
  );
}

// --- Live TV custom channels -------------------------------------------------------

// Admin-added live channels: any http(s) stream URL (HLS m3u8 or direct
// mp4/etc). Shown in Live TV as the "My Channels" tab; stored server-side in
// .torrent-cache/livetv-custom.json so every visitor sees them.
function LiveTvChannelsCard({ headers }) {
  const [channels, setChannels] = useState(null);
  const [form, setForm] = useState({ name: "", url: "", logo: "", group: "" });
  const [notice, setNotice] = useState(null);

  const loadList = useCallback(() => {
    fetch("/api/livetv/custom")
      .then((r) => r.json())
      .then((d) => setChannels(d.channels || []))
      .catch(() => setChannels([]));
  }, []);
  useEffect(loadList, [loadList]);

  const add = async (e) => {
    e.preventDefault();
    setNotice(null);
    const r = await fetch("/api/livetv/custom", { method: "POST", headers: headers(), body: JSON.stringify(form) });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) {
      setNotice(d.error || "Could not add channel");
      return;
    }
    setForm({ name: "", url: "", logo: "", group: "" });
    setNotice(`Added “${d.channel?.name}” — it now appears in Live TV under My Channels.`);
    loadList();
  };

  const remove = async (key) => {
    const r = await fetch(`/api/livetv/custom?key=${encodeURIComponent(key)}`, { method: "DELETE", headers: headers() });
    if (r.ok) loadList();
  };

  return (
    <div className="admin-card">
      <h2>📺 Live TV — my channels</h2>
      <p className="muted small">
        Add any public stream URL (HLS .m3u8 or a direct .mp4/.mkv link). It appears for every visitor in
        Live TV → “My Channels”. Use for FTA channels you want featured, radio streams, or your own feeds.
      </p>
      <form className="admin-livetv-form" onSubmit={add}>
        <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Channel name *" required />
        <input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="Stream URL (https://…/index.m3u8) *" required />
        <input value={form.logo} onChange={(e) => setForm({ ...form, logo: e.target.value })} placeholder="Logo URL (optional)" />
        <input value={form.group} onChange={(e) => setForm({ ...form, group: e.target.value })} placeholder="Group (optional)" />
        <button className="btn btn-primary" type="submit">Add channel</button>
      </form>
      {notice && <p className="muted small">{notice}</p>}
      {channels && channels.length > 0 && (
        <ul className="admin-livetv-list">
          {channels.map((c) => (
            <li key={c.key}>
              <span className="admin-livetv-name">{c.name}</span>
              <span className="admin-livetv-url" title={c.url}>{c.url}</span>
              <button className="btn btn-ghost btn-sm" onClick={() => remove(c.key)}>Remove</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// --- YouTube mappings ---------------------------------------------------------------

// Paste a YouTube link for a TMDB title (official full-movie uploads). The
// movie then plays YouTube's own embed BEFORE falling back to Vidking. The
// server probes every video via oEmbed: deleted/private videos are flagged
// dead (red) and the movie silently falls back to the normal provider chain.
function YouTubeCard({ headers }) {
  const [mappings, setMappings] = useState(null);
  const [form, setForm] = useState({ query: "", url: "" });
  const [picked, setPicked] = useState(null); // { tmdbId, title }
  const [results, setResults] = useState([]);
  const [notice, setNotice] = useState(null);
  const [checking, setChecking] = useState(false);

  const loadList = useCallback(() => {
    fetch("/api/youtube/list")
      .then((r) => r.json())
      .then((d) => setMappings(d.mappings || []))
      .catch(() => setMappings([]));
  }, []);
  useEffect(loadList, [loadList]);

  // Debounced title search; raw digits = TMDB id.
  useEffect(() => {
    const q = form.query.trim();
    if (!q || picked) return undefined;
    const t = setTimeout(async () => {
      try {
        if (/^\d+$/.test(q) && q.length >= 2) {
          const d = await tmdb.movieDetails(Number(q)).catch(() => null);
          setResults(d ? [{ tmdbId: d.tmdbId, title: d.title, year: (d.releaseDate || "").slice(0, 4) }] : []);
        } else {
          const page = await tmdb.searchMulti(q, 1);
          setResults(
            toCards(page.results)
              .filter((c) => c.contentType === ContentType.MOVIE)
              .slice(0, 6)
              .map((c) => ({ tmdbId: c.tmdbId, title: c.title, year: (c.date || "").slice(0, 4) })),
          );
        }
      } catch {
        setResults([]);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [form.query, picked]);

  const map = async (e) => {
    e.preventDefault();
    setNotice(null);
    if (!picked) {
      setNotice("Pick a title from the search results first.");
      return;
    }
    const r = await fetch("/api/youtube/map", {
      method: "POST",
      headers: headers(),
      body: JSON.stringify({ type: "movie", id: picked.tmdbId, url: form.url, title: picked.title }),
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) {
      setNotice(d.error || "Could not map that link");
      return;
    }
    setForm({ query: "", url: "" });
    setPicked(null);
    setResults([]);
    setNotice("Mapped — that movie now plays from YouTube.");
    loadList();
  };

  const unmap = async (key) => {
    const [kind, id, season, episode] = key.split(":");
    const r = await fetch("/api/youtube/unmap", {
      method: "POST",
      headers: headers(),
      body: JSON.stringify({ type: kind === "tv" ? "episode" : "movie", id: Number(id), season: season ? Number(season) : null, episode: episode ? Number(episode) : null }),
    });
    if (r.ok) loadList();
  };

  const recheck = async () => {
    setChecking(true);
    await fetch("/api/youtube/check", { method: "POST", headers: headers() }).catch(() => {});
    setChecking(false);
    loadList();
  };

  return (
    <div className="admin-card">
      <h2>▶️ YouTube — official uploads</h2>
      <p className="muted small">
        Map an official YouTube full-movie link to a title. It plays in YouTube's own player before the normal
        sources. If the video is later deleted, the movie automatically falls back to the usual provider and the
        entry shows as DEAD here so you can paste a new link.
      </p>
      <form className="admin-yt-form" onSubmit={map}>
        <div className="admin-yt-picker">
          <input
            value={picked ? `${picked.title} (${picked.year || ""})` : form.query}
            onChange={(e) => {
              setPicked(null);
              setForm({ ...form, query: e.target.value });
            }}
            placeholder="Movie title or TMDB id…"
            required
          />
          {!picked && results.length > 0 && (
            <div className="admin-yt-results">
              {results.map((r) => (
                <button key={r.tmdbId} type="button" onClick={() => setPicked(r)}>
                  {r.title} {r.year ? `(${r.year})` : ""}
                </button>
              ))}
            </div>
          )}
        </div>
        <input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://www.youtube.com/watch?v=… *" required />
        <button className="btn btn-primary" type="submit">Map</button>
      </form>
      {notice && <p className="muted small">{notice}</p>}
      {mappings && mappings.length > 0 && (
        <>
          <div className="admin-yt-tools">
            <button className="btn btn-ghost btn-sm" onClick={recheck} disabled={checking}>
              {checking ? "Checking…" : "Re-check all links now"}
            </button>
          </div>
          <ul className="admin-livetv-list">
            {mappings.map((m) => (
              <li key={m.key} className={m.dead ? "dead" : ""}>
                <span className="admin-livetv-name">{m.dead ? "☠ " : "✓ "}{m.title || m.key}</span>
                <span className="admin-livetv-url" title={`https://www.youtube.com/watch?v=${m.videoId}`}>
                  youtube.com/watch?v={m.videoId}{m.dead ? " — REMOVED on YouTube (playing fallback)" : ""}
                </span>
                <button className="btn btn-ghost btn-sm" onClick={() => unmap(m.key)}>Remove</button>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

// --- hosted files (Cloudflare / StreamHG / VidMoly) ---------------------------------

// Paste a StreamHG or VidMoly link (or a Cloudflare package URL) for a TMDB
// title. The server
// extracts the host's direct HLS stream and the movie plays in PoKu's own
// player (no host ads). If extraction fails, the host's embed plays instead.
// A dead file falls through to the normal chain automatically.
// Hybrid playback: the video is hosted by a video host (StreamHG or VidMoly) while
// the audio languages and subtitles come from PoKu's own HLS package for the
// same title. Nothing extra is typed per language — the audio master already
// declares every rendition, so the admin only points at the host's file.
function HybridCard({ headers }) {
  const [mappings, setMappings] = useState([]);
  const [form, setForm] = useState({ query: "", url: "", account: "B", audioMaster: "", audio: "" });
  const [picked, setPicked] = useState(null);
  const [results, setResults] = useState([]);
  const [notice, setNotice] = useState(null);
  const [shg, setShg] = useState(null); // { configured, account?, error? }
  const [vm, setVm] = useState(null); // VidMoly account badge

  const loadList = useCallback(() => {
    fetch("/api/hosted/list")
      .then((r) => r.json())
      .then((d) => setMappings(d.mappings || []))
      .catch(() => setMappings([]));
  }, []);
  useEffect(loadList, [loadList]);

  // Host account badges in the card header (same endpoints as HostedFileCard).
  const loadShg = useCallback(() => {
    fetch("/api/hosted/streamhg-account", { headers: headers() })
      .then((r) => r.json())
      .then(setShg)
      .catch(() => setShg(null));
  }, []);
  useEffect(loadShg, [loadShg]);

  const loadVm = useCallback(() => {
    fetch("/api/hosted/vidmoly-account", { headers: headers() })
      .then((r) => r.json())
      .then(setVm)
      .catch(() => setVm(null));
  }, []);
  useEffect(loadVm, [loadVm]);

  useEffect(() => {
    const q = form.query.trim();
    if (!q || picked) return undefined;
    const t = setTimeout(async () => {
      try {
        if (/^\d+$/.test(q) && q.length >= 2) {
          const d = await tmdb.movieDetails(Number(q)).catch(() => null);
          setResults(d ? [{ tmdbId: d.tmdbId, title: d.title, year: (d.releaseDate || "").slice(0, 4) }] : []);
        } else {
          const page = await tmdb.searchMulti(q, 1);
          setResults(
            toCards(page.results)
              .filter((c) => c.contentType === ContentType.MOVIE)
              .slice(0, 6)
              .map((c) => ({ tmdbId: c.tmdbId, title: c.title, year: (c.date || "").slice(0, 4) })),
          );
        }
      } catch {
        setResults([]);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [form.query, picked]);

  const map = async (e) => {
    e.preventDefault();
    setNotice(null);
    if (!picked) {
      setNotice("Pick a title from the search results first.");
      return;
    }
    const r = await fetch("/api/hosted/hybrid", {
      method: "POST",
      headers: headers(),
      body: JSON.stringify({
        type: "movie",
        id: picked.tmdbId,
        url: form.url,
        account: form.account,
        audioMaster: form.audioMaster,
        audio: form.audio,
        title: picked.title,
      }),
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) {
      setNotice(d.error || "Could not add the hybrid link");
      return;
    }
    setForm({ query: "", url: "", account: form.account, audioMaster: "", audio: "" });
    setPicked(null);
    setResults([]);
    setNotice("Hybrid link added — video from the host, audio and subtitles from your own package.");
    loadList();
  };

  const removeLink = async (key, linkId) => {
    const [kind, id, season, episode] = key.split(":");
    const r = await fetch("/api/hosted/link/remove", {
      method: "POST",
      headers: headers(),
      body: JSON.stringify({ type: kind === "tv" ? "episode" : "movie", id: Number(id), season: season ? Number(season) : null, episode: episode ? Number(episode) : null, linkId }),
    });
    if (r.ok) loadList();
  };

  const hybridEntries = mappings
    .map((m) => ({ ...m, links: (m.links || []).filter((l) => l.kind === "hybrid") }))
    .filter((m) => m.links.length > 0);

  return (
    <div className="admin-card">
      <h2>
        🧩 Hybrid source — host video + your audio
        {[["⬡ StreamHG", shg], ["▣ VidMoly", vm]].map(([label, acct]) =>
          acct?.configured ? (
            <span key={label} className="muted small" style={{ marginLeft: 10, fontWeight: 400 }}>
              {acct.account && !acct.error ? "🟢" : "🔴"} {label}
            </span>
          ) : null,
        )}
      </h2>
      <p className="muted small">
        Point at a video already uploaded to a host, and the movie plays that video while the
        <strong> audio languages and subtitles come from your own HLS package</strong> for the same title.
        That keeps every audio track and the subtitles (hosts re-encode to one track and drop subs), costs roughly
        a sixth of the space of a full package, and skips the host's player and its ads entirely. Leave the audio
        master blank to reuse the package already mapped for this title; the signed video link is minted on the
        server at playback time, so host credentials never reach the browser.
      </p>
      <form className="admin-yt-form" onSubmit={map}>
        <div className="admin-yt-picker">
          <input
            value={picked ? `${picked.title} (${picked.year || ""})` : form.query}
            onChange={(e) => {
              setPicked(null);
              setForm({ ...form, query: e.target.value });
            }}
            placeholder="Movie title or TMDB id…"
            required
          />
          {!picked && results.length > 0 && (
            <div className="admin-yt-results">
              {results.map((r) => (
                <button key={r.tmdbId} type="button" onClick={() => setPicked(r)}>
                  {r.title} {r.year ? `(${r.year})` : ""}
                </button>
              ))}
            </div>
          )}
        </div>
        <input
          value={form.url}
          onChange={(e) => setForm({ ...form, url: e.target.value })}
          placeholder="https://hgcloud.to/… or https://vidmoly.me/v/… *"
          required
        />
        <input
          value={form.audioMaster}
          onChange={(e) => setForm({ ...form, audioMaster: e.target.value })}
          placeholder="Audio master .m3u8 (optional — reuses this title's package)"
        />
        <input
          value={form.audio}
          onChange={(e) => setForm({ ...form, audio: e.target.value })}
          placeholder="Default audio (optional — e.g. Malayalam)"
          list="hosted-audio-langs"
        />
        <button className="btn btn-primary" type="submit">Add hybrid link</button>
      </form>
      {notice && <p className="muted small">{notice}</p>}
      {hybridEntries.length > 0 && (
        <ul className="admin-list">
          {hybridEntries.map((m) => (
            <li key={m.key}>
              <strong>{m.title || m.key}</strong>
              {m.links.map((l) => (
                <span key={l.id} className="muted small" style={{ marginLeft: 8 }}>
                  {l.host} · {l.fileId} · acct {l.account || "A"} · default {l.audio || "(package default)"}
                  {l.deadAt ? " · flagged dead" : ""}
                  <button className="btn btn-ghost btn-sm" style={{ marginLeft: 8 }} onClick={() => removeLink(m.key, l.id)}>
                    Remove
                  </button>
                </span>
              ))}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// One language + one link + its optional magnet. Both wizard modes are built
// from this: Single is a single row, Multi is a list of them. The language box
// is filled in from TMDB when the title has a record, and stays the admin's to
// change — a Tamil dub of a Malayalam film is normal, and the player tries the
// links in the order they were added.
function AddLinkRow({ row, onChange, onAdd, onRemove, busy, submitLabel, showRemove }) {
  return (
    <div className="admin-link-row">
      <div className="admin-episode-fields">
        <label>
          Language
          <input
            value={row.language}
            onChange={(e) => onChange({ ...row, language: e.target.value })}
            placeholder="Malayalam"
            list="hosted-audio-langs"
          />
        </label>
        <input
          value={row.url}
          onChange={(e) => onChange({ ...row, url: e.target.value })}
          placeholder="https://hgcloud.to/… · https://vidmoly.me/v/… · https://tapeadsenjoyer.com/v/… *"
        />
        <input value={row.magnet} onChange={(e) => onChange({ ...row, magnet: e.target.value })} placeholder="magnet:?xt=… (optional)" />
      </div>
      <div className="admin-episode-actions">
        <button className="btn btn-primary" onClick={onAdd} disabled={busy}>
          {busy ? "Adding…" : submitLabel}
        </button>
        {showRemove && (
          <button className="btn btn-ghost btn-sm" onClick={onRemove}>
            Remove this row
          </button>
        )}
      </div>
    </div>
  );
}

// A subtitle, added to one title or one episode, from wherever it comes:
// a pasted .srt URL, a file from this machine, or SubDL's public database.
//
// It is aimed at a target — `{ type, id, season, episode, title }` — because
// that is where it is opened from: a `S08E01` row in the folder tree, or a movie
// row. Every route it calls already speaks episodes (`type=episode` with season
// and episode), so nothing here has to invent a key.
function SubtitleForm({ target, headers, onChanged }) {
  const [lang, setLang] = useState("");
  const [url, setUrl] = useState("");
  const [fileText, setFileText] = useState(null);
  const [fileName, setFileName] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [online, setOnline] = useState({ items: null, searching: false, attaching: null, error: null });

  const where = target.type === "episode" ? episodeLabel(target.season, target.episode) : target.title;

  const readSrt = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setFileText(String(reader.result || ""));
      setFileName(file.name);
      setUrl("");
    };
    reader.readAsText(file);
  };

  // One place for both "paste" and "upload": the server takes either the text or
  // the URL and does the fetch + VTT conversion itself, which is why a pasted
  // link works from the deployed site (no browser CORS, no file to move).
  const add = async (e) => {
    e.preventDefault();
    setNotice(null);
    if (!fileText && !url.trim()) {
      setNotice("Paste a subtitle link, or choose a file.");
      return;
    }
    setBusy(true);
    try {
      const r = await fetch("/api/subs/add", {
        method: "POST",
        headers: headers(),
        body: JSON.stringify({
          type: target.type,
          id: target.id,
          season: target.season ?? null,
          episode: target.episode ?? null,
          lang: lang || "English",
          title: target.title,
          content: fileText,
          importUrl: fileText ? null : url.trim(),
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        setNotice(d.error || "Could not add that subtitle");
        return;
      }
      setUrl("");
      setFileText(null);
      setFileName("");
      setNotice(`${where} — ${lang || "English"} subtitle added. It plays as a CC option from now on.`);
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  const findOnline = async () => {
    setOnline({ items: null, searching: true, attaching: null, error: null });
    try {
      const qs = new URLSearchParams({
        type: target.type,
        id: String(target.id),
        lang: (lang || "english").toLowerCase(),
        ...(target.type === "episode" ? { season: String(target.season), episode: String(target.episode) } : {}),
      });
      const r = await fetch(`/api/subs/podnapisi/search?${qs}`, { headers: headers() });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || "Search failed");
      setOnline({
        items: d.results || [],
        searching: false,
        attaching: null,
        error: (d.results || []).length ? null : `No subtitles found for ${where} in ${lang || "English"} — try another language, or paste a link.`,
      });
    } catch (e) {
      setOnline({ items: [], searching: false, attaching: null, error: e.message });
    }
  };

  const attachOnline = async (item) => {
    setOnline((o) => ({ ...o, attaching: item.id, error: null }));
    try {
      const r = await fetch("/api/subs/podnapisi/add", {
        method: "POST",
        headers: headers(),
        body: JSON.stringify({
          subdlUrl: item.url,
          type: target.type,
          id: target.id,
          season: target.season ?? null,
          episode: target.episode ?? null,
          lang: lang || "English",
          title: target.title,
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || "Could not attach that subtitle");
      setOnline({ items: null, searching: false, attaching: null, error: null });
      setNotice(`${where} — attached “${item.name}”.`);
      onChanged();
    } catch (e) {
      setOnline((o) => ({ ...o, attaching: null, error: e.message }));
    }
  };

  return (
    <div className="admin-subs-inline">
      <p className="admin-breadcrumb">
        💬 Subtitle for <strong>{where}</strong>
      </p>
      <form onSubmit={add}>
        <div className="admin-episode-fields">
          <label>
            Language
            <input value={lang} onChange={(e) => setLang(e.target.value)} placeholder="English" list="hosted-audio-langs" />
          </label>
          <label className="admin-subs-file">
            <input type="file" accept=".srt,.vtt,text/plain" onChange={(e) => readSrt(e.target.files?.[0])} />
            {fileName ? `📄 ${fileName}` : "Choose a file…"}
          </label>
          <input
            value={url}
            onChange={(e) => {
              setUrl(e.target.value);
              setFileText(null);
              setFileName("");
            }}
            placeholder="…or paste a direct .srt / .vtt link"
          />
        </div>
        <div className="admin-episode-actions">
          <button className="btn btn-primary" type="submit" disabled={busy}>
            {busy ? "Adding…" : "Add subtitle"}
          </button>
          <button className="btn btn-ghost" type="button" onClick={findOnline} disabled={online.searching || busy}>
            {online.searching ? "Searching…" : "🌐 Find online (SubDL)"}
          </button>
        </div>
      </form>
      {online.error && <p className="muted small">{online.error}</p>}
      {online.items && online.items.length > 0 && (
        <ul className="admin-livetv-list">
          {online.items.map((item) => (
            <li key={item.id}>
              <span className="admin-livetv-name">🌐 {item.releaseName || item.name}</span>
              <span className="admin-livetv-url">
                {item.language}
                {item.hi ? " · HI" : ""}
              </span>
              <button className="btn btn-ghost btn-sm" onClick={() => attachOnline(item)} disabled={online.attaching != null}>
                {online.attaching === item.id ? "Attaching…" : "Attach"}
              </button>
            </li>
          ))}
        </ul>
      )}
      {notice && <p className="muted small">{notice}</p>}
    </div>
  );
}

// The subtitle tracks attached to one row: what plays, and a way to take it off.
function SubtitleTracks({ node, headers, onChanged }) {
  const [busy, setBusy] = useState(false);
  const tracks = node?.tracks || [];

  const remove = async (trackId) => {
    setBusy(true);
    try {
      const parsed = parseHostedKey(node.key);
      await fetch("/api/subs/remove", {
        method: "POST",
        headers: headers(),
        body: JSON.stringify({
          type: parsed?.kind === "tv" ? "episode" : "movie",
          id: parsed?.id,
          season: parsed?.season ?? null,
          episode: parsed?.episode ?? null,
          trackId,
        }),
      });
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  return (
    <ul className="admin-subs-tracks">
      {tracks.map((t) => (
        <li key={t.id}>
          <span className="admin-hosted-audio">💬 {t.label || t.lang}</span>
          <span className="admin-livetv-url" title={t.file}>
            {t.file}
          </span>
          <button className="btn btn-ghost btn-sm" onClick={() => remove(t.id)} disabled={busy}>
            Remove track
          </button>
        </li>
      ))}
    </ul>
  );
}

/**
 * Copies whose retention window has closed (hosts.js).
 *
 * They are gone from the site and still here — that is the whole design: the
 * clock HIDES, it never deletes, and a copy the operator wants back is one Renew
 * away from being listed again. VidMoly's multi-audio copy is the only host with
 * a window today (365 days); the per-language StreamTape tiles are permanent.
 */
function RetentionCard({ headers, onRenewed }) {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    fetch("/api/hosted/retention", { headers: headers() })
      .then((r) => (r.ok ? r.json() : { expired: [] }))
      .then((d) => setRows(d.expired || []))
      .catch(() => setRows([]));
  }, [headers]);
  useEffect(load, [load]);

  const renew = async (row) => {
    setBusy(row.id);
    setError(null);
    try {
      const [kind, id, season, episode] = String(row.key).split(":");
      const r = await fetch("/api/hosted/link/renew", {
        method: "POST",
        headers: headers(),
        body: JSON.stringify({
          type: kind === "tv" ? "episode" : "movie",
          id: Number(id),
          season: season ? Number(season) : null,
          episode: episode ? Number(episode) : null,
          linkId: row.id,
        }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`);
      load();
      onRenewed?.();
    } catch (e) {
      setError(e.message || "Renew failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="admin-folders">
      <div className="admin-folders-head">
        <strong>⏳ Retention ({rows ? rows.length : "…"})</strong>
        <span className="muted small">
          VidMoly copies are kept 365 days and then leave the site; StreamTape copies are permanent. Nothing is
          deleted — Renew restarts the clock.
        </span>
      </div>
      {error && <p className="source-check bad">{error}</p>}
      {rows && rows.length === 0 && <p className="muted small">Nothing has expired yet.</p>}
      {rows && rows.length > 0 && (
        <ul className="admin-hosted-links">
          {rows.map((row) => (
            <li key={`${row.key}:${row.id}`}>
              <span className="admin-hosted-host" title="Host">
                {row.host === "vidmoly" ? "▣ VM" : row.host}
              </span>
              <span className="admin-livetv-name">{row.title || row.key}</span>
              <span className="muted small">
                {row.key} · {row.audio || "audio?"} · expired {row.expiresAt ? new Date(row.expiresAt).toLocaleDateString() : "—"}
                {row.daysOverdue ? ` · ${row.daysOverdue}d ago` : ""}
              </span>
              <button className="btn btn-ghost btn-sm" onClick={() => renew(row)} disabled={busy === row.id}>
                {busy === row.id ? "Renewing…" : "Renew"}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function HostedFileCard({ headers }) {
  const [mappings, setMappings] = useState(null);
  const [notice, setNotice] = useState(null);
  const [shg, setShg] = useState(null); // { configured, account?, error? }
  const [vm, setVm] = useState(null); // VidMoly account badge
  const [alive, setAlive] = useState(null); // key -> true|false|null
  const [checking, setChecking] = useState(false);
  const [copied, setCopied] = useState(null);

  // --- folder browser -------------------------------------------------------
  // Shows are folders, seasons are folders inside them, episodes are the files.
  // None of that is stored: the tree is folded out of the mapping keys on every
  // render (hosted-folders.js), so these are only what the operator has *open*,
  // plus the season numbers of folders they have created but not filled yet.
  const [openShow, setOpenShow] = useState(null); // tmdbId
  const [openSeason, setOpenSeason] = useState(null); // season number
  const [blankSeasons, setBlankSeasons] = useState({}); // showId -> [season numbers]
  const [seasonDraft, setSeasonDraft] = useState(""); // "New folder" -> season number
  const [showPicker, setShowPicker] = useState(false); // root "New folder" search
  const [showQuery, setShowQuery] = useState("");
  const [showResults, setShowResults] = useState([]);
  const [otherLanguage, setOtherLanguage] = useState(""); // “Other” has no TMDB language to read

  // --- "Add link" wizard ----------------------------------------------------
  // One entry point for every kind of link: Movies / TV Shows / Other, then the
  // title, then Single or Multi, then the link(s). Opened from the card header
  // (nothing known yet) or from inside a season folder (show and season already
  // known, so it opens at the episode step).
  const [add, setAdd] = useState(null); // { kind, target, mode, season, episode, preset }
  const [addQuery, setAddQuery] = useState("");
  const [addResults, setAddResults] = useState([]);
  const [addBusy, setAddBusy] = useState(false);
  const [addRows, setAddRows] = useState([]); // multi: one row per language+link
  const [libraryTitles, setLibraryTitles] = useState([]); // titles of ours (no TMDB record)
  const [subs, setSubs] = useState([]); // the other store: subtitle tracks, same key space
  const [subTarget, setSubTarget] = useState(null); // the row whose 💬 form is open

  const tree = buildHostedTree(mappings, { subtitles: subs });

  // Our own titles, once: the wizard needs their language for the auto-detect
  // and their names for the search, and the route is public.
  useEffect(() => {
    let cancelled = false;
    fetch("/api/library/list")
      .then((r) => r.json())
      .then((d) => {
        if (!cancelled) setLibraryTitles(d.titles || []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // Open a season folder. Browsing and adding are separate: the folder shows
  // what is there, and "＋ Add" (or a gap row) opens the wizard pointed at it.
  const openSeasonFolder = (showId, season) => {
    setOpenShow(showId);
    setOpenSeason(season);
    setSeasonDraft("");
  };

  // The wizard, already aimed at one episode: no kind, no title search, just the
  // episode number and the link. The season and the show are already known.
  const addToEpisode = (showId, title, season, episode) => {
    openSeasonFolder(showId, season);
    const known = tree.shows.find((s) => s.id === showId)?.seasons.find((s) => s.season === season);
    const next = episode != null ? episode : known ? known.nextEpisode : 1;
    setAdd({
      kind: "tv",
      target: { id: showId, title, contentType: ContentType.TV_SHOW, language: libraryLanguage(showId) },
      mode: null,
      season,
      episode: String(next),
      preset: true,
    });
    setAddRows([]);
  };

  // "New folder" inside a show. An empty season is client state until something
  // is added to it, at which point the key makes it permanent.
  const createSeason = (showId, title, season) => {
    const n = Number(season);
    if (!Number.isFinite(n) || n < 1) {
      setNotice("Which season? Enter the number, e.g. 9.");
      return;
    }
    setBlankSeasons((prev) => (prev[showId]?.includes(n) ? prev : { ...prev, [showId]: [...(prev[showId] || []), n] }));
    openSeasonFolder(showId, n);
    setNotice(`${title} — Season ${n} created. Use “＋ Add” to paste the first episode.`);
  };

  // "New folder" at the root: find the show, then open it with a first season.
  const createShow = (pick) => {
    const existing = tree.shows.find((s) => s.id === Number(pick.id));
    if (existing) {
      openSeasonFolder(existing.id, existing.seasons[existing.seasons.length - 1].season);
      return;
    }
    setOpenShow(Number(pick.id));
    setOpenSeason(null);
    createSeason(Number(pick.id), pick.title, 1);
  };

  // Show search for a new folder: our own titles first (TMDB has never heard of
  // Big Boss), then TMDB shows.
  useEffect(() => {
    const q = showQuery.trim();
    if (!showPicker || !q) {
      setShowResults([]);
      return undefined;
    }
    const t = setTimeout(async () => {
      const found = [];
      const needle = q.toLowerCase();
      try {
        const r = await fetch("/api/library/list");
        const d = await r.json();
        for (const t2 of d.titles || []) {
          if (t2.contentType !== "TV") continue;
          if (String(t2.title || "").toLowerCase().includes(needle)) {
            found.push({ id: Number(t2.id), title: t2.title, year: "", source: "your titles" });
          }
        }
      } catch {
        // No library route on an older deploy — TMDB alone still works.
      }
      try {
        const page = await tmdb.searchMulti(q, 1);
        for (const c of toCards(page.results).filter((c) => c.contentType === ContentType.TV_SHOW).slice(0, 6)) {
          if (!found.some((f) => f.id === c.tmdbId)) found.push({ id: c.tmdbId, title: c.title, year: (c.date || "").slice(0, 4) });
        }
      } catch {
        // Offline: our own titles are still offered.
      }
      setShowResults(found.slice(0, 8));
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [showQuery, showPicker]);

  // ------------------------------------------------------------------ wizard --

  // Our own titles (the ones TMDB has never heard of) and their language, used
  // for the language auto-detect and for the “Other” step.
  const libraryLanguage = (id) => libraryTitles.find((t) => Number(t.id) === Number(id))?.language || null;

  const closeAdd = () => {
    setAdd(null);
    setAddQuery("");
    setAddResults([]);
    setAddRows([]);
    setAddBusy(false);
  };

  const startAdd = (kind) => {
    closeAdd();
    setAdd({ kind, target: null, mode: null, season: "", episode: "", preset: false });
  };

  // Pick a title from the search (or from a raw TMDB id) and read its language
  // out of TMDB, so “Single” starts with the right one already filled in. The
  // admin can still overwrite it — a Hindi dub of a Malayalam film is normal.
  const pickTitle = async (pick) => {
    setAddBusy(true);
    let language = pick.language || null;
    try {
      const dto = pick.kind === "tv" ? await tmdb.tvDetails(pick.id) : await tmdb.movieDetails(pick.id);
      const code = dto?.original_language || dto?.originalLanguage || null;
      if (code) language = languageName(code) || language;
    } catch {
      // No TMDB record (a title of ours, or TMDB is down): keep what we have.
    }
    setAddBusy(false);
    setAddResults([]);
    setAddQuery("");
    setAdd((a) => ({ ...a, target: { ...pick, language } }));
  };

  // “Other”: a title nobody has catalogued. Creates the manual library record
  // (or reuses the one with the same name) and hands back its reserved id, so
  // the rest of PoKu — mappings, resolve, the app's library — treats it like any
  // other title without a second key space.
  const createManualTitle = async () => {
    const title = addQuery.trim();
    if (!title) {
      setNotice("What is it called? Type the name you want to see in the app.");
      return;
    }
    setAddBusy(true);
    try {
      const r = await fetch("/api/library", {
        method: "POST",
        headers: headers(),
        body: JSON.stringify({ title, contentType: add.kind === "other-tv" ? "TV" : "MOVIE", language: otherLanguage || null }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        setNotice(d.error || "Could not create that title");
        return;
      }
      const t = d.title;
      setAdd((a) => ({
        ...a,
        kind: a.kind === "other-tv" ? "tv" : "movie",
        target: { id: Number(t.id), title: t.title, contentType: t.contentType, language: t.language || otherLanguage || null, manual: true },
      }));
      setAddQuery("");
    } catch {
      setNotice("Could not reach the server.");
    } finally {
      setAddBusy(false);
    }
  };

  // Post one language+link pair. Both modes end here: Single posts once, Multi
  // posts once per row and leaves the row ready for the next language.
  const addLink = async ({ language, url, magnet }) => {
    if (!add?.target) return;
    const clean = String(url || "").trim();
    if (!/^https?:\/\//i.test(clean)) {
      setNotice("Paste the full link, starting with https://");
      return false;
    }
    const isEpisode = add.kind === "tv" && add.season !== "" && add.episode !== "";
    if (add.kind === "tv" && !isEpisode) {
      setNotice("A show needs a season and an episode — fill both in.");
      return false;
    }
    setAddBusy(true);
    try {
      const r = await fetch("/api/hosted/map", {
        method: "POST",
        headers: headers(),
        body: JSON.stringify({
          type: isEpisode ? "episode" : "movie",
          id: add.target.id,
          season: isEpisode ? Number(add.season) : null,
          episode: isEpisode ? Number(add.episode) : null,
          url: clean,
          audio: String(language || "").trim() || null,
          title: add.target.title,
          magnet: magnet || null,
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        setNotice(d.error || "Could not add that link");
        return false;
      }
      const where = isEpisode
        ? `${add.target.title} ${episodeLabel(Number(add.season), Number(add.episode))}`
        : add.target.title;
      setNotice(`${where} — ${language || "link"} added.`);
      loadList();
      return true;
    } catch {
      setNotice("Could not reach the server.");
      return false;
    } finally {
      setAddBusy(false);
    }
  };

  // Title search for the wizard: raw digits are a TMDB id, anything else is a
  // name — our own titles first, then TMDB.
  useEffect(() => {
    const q = addQuery.trim();
    if (!add || add.target || add.preset || add.kind === "other" || !q) {
      setAddResults([]);
      return undefined;
    }
    const t = setTimeout(async () => {
      const found = [];
      const needle = q.toLowerCase();
      if (/^\d+$/.test(q) && q.length >= 2) {
        try {
          const dto = add.kind === "tv" ? await tmdb.tvDetails(Number(q)) : await tmdb.movieDetails(Number(q));
          if (dto) found.push({ id: Number(dto.id), title: dto.title || dto.name || `TMDB ${q}`, year: "", kind: add.kind });
        } catch {
          setNotice(`TMDB has no ${add.kind === "tv" ? "show" : "film"} with id ${q}.`);
        }
      } else {
        for (const t2 of libraryTitles) {
          if (add.kind === "tv" ? t2.contentType !== "TV" : t2.contentType === "TV") continue;
          if (String(t2.title || "").toLowerCase().includes(needle)) {
            found.push({ id: Number(t2.id), title: t2.title, year: "", language: t2.language, kind: add.kind, manual: true });
          }
        }
        try {
          const page = await tmdb.searchMulti(q, 1);
          const want = add.kind === "tv" ? ContentType.TV_SHOW : ContentType.MOVIE;
          for (const c of toCards(page.results).filter((c) => c.contentType === want).slice(0, 6)) {
            if (!found.some((f) => f.id === c.tmdbId)) found.push({ id: c.tmdbId, title: c.title, year: (c.date || "").slice(0, 4), kind: add.kind });
          }
        } catch {
          // Offline: our own titles are still offered.
        }
      }
      setAddResults(found.slice(0, 8));
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [addQuery, add, libraryTitles]);

  // The per-link rows (status, audio, host, url, magnet copy, remove) — one
  // implementation, used by a movie row and by an episode row alike.
  const linkRows = (m) => (
    <ul className="admin-hosted-links">
      {(m.links || []).map((l) => {
        const isDead = l.deadAt || alive?.[l.id] === false;
        const isAlive = alive?.[l.id] === true;
        return (
          <li key={l.id} className={isDead ? "dead" : isAlive ? "alive" : ""}>
            <span className="admin-hosted-status" title={isDead ? "Dead" : isAlive ? "Alive" : "Unknown"}>
              {isDead ? "🔴" : isAlive ? "🟢" : "❓"}
            </span>
            <span className="admin-hosted-audio" title="Audio language">🎙 {l.audio || "audio?"}</span>
            <span className="admin-hosted-host" title="Host">{l.host === "streamhg" ? "⬡ SH" : l.host === "vidmoly" ? "▣ VM" : l.host === "hls" ? "☁ CF" : l.host === "streamtape" ? "⛔ ST" : "?"}</span>
            <span className="admin-livetv-url" title={l.url}>
              {l.url.replace(/^https?:\/\//, "").slice(0, 44)}
              {isDead ? " — DEAD: copy magnet → upload → paste new link" : l.magnet ? " · magnet kept" : ""}
            </span>
            {l.magnet && isDead && (
              <button className="btn btn-ghost btn-sm" onClick={() => copyMagnet(l.id, l.magnet)}>
                {copied === l.id ? "Copied ✓" : "Copy magnet"}
              </button>
            )}
            <button className="btn btn-ghost btn-sm" onClick={() => removeLink(m.key, l.id)}>Remove link</button>
          </li>
        );
      })}
    </ul>
  );

  const loadList = useCallback(() => {
    fetch("/api/hosted/list")
      .then((r) => r.json())
      .then((d) => setMappings(d.mappings || []))
      .catch(() => setMappings([]));
  }, []);
  useEffect(loadList, [loadList]);

  // The subtitle store, same key space, so a title's CC tracks sit under the row
  // that plays it. Reloaded with the links — adding a subtitle and pasting a link
  // are the same kind of edit.
  const loadSubs = useCallback(() => {
    fetch("/api/subs/list")
      .then((r) => r.json())
      .then((d) => setSubs(d.mappings || []))
      .catch(() => setSubs([]));
  }, []);
  useEffect(loadSubs, [loadSubs]);

  // Open the 💬 form for one row. The mapping key is everything the routes need —
  // `movie:155` or `tv:900000001:8:1`.
  const openSubForm = (key, title, type) => {
    setSubTarget({ key, title, type, ...(parseHostedKey(key) || {}) });
  };

  // Host account badges (shown in the card header once configured).
  const loadShg = useCallback(() => {
    fetch("/api/hosted/streamhg-account", { headers: headers() })
      .then((r) => r.json())
      .then(setShg)
      .catch(() => setShg(null));
  }, []);
  useEffect(loadShg, [loadShg]);

  const loadVm = useCallback(() => {
    fetch("/api/hosted/vidmoly-account", { headers: headers() })
      .then((r) => r.json())
      .then(setVm)
      .catch(() => setVm(null));
  }, []);
  useEffect(loadVm, [loadVm]);

  // Ask each host's API whether every mapped file still exists. Dead ones get
  // flagged server-side (deadAt) and show here until re-mapped.
  const checkHosts = async () => {
    setChecking(true);
    setNotice(null);
    try {
      const r = await fetch("/api/hosted/host-status", { headers: headers() });
      const d = await r.json();
      if (!d.configured) {
        setNotice("Add STREAMHG_API_KEY or VIDMOLY_API_KEY to web/.env to enable health checks.");
        return;
      }
      // alive map is keyed by linkId now (one link per row).
      setAlive(Object.fromEntries((d.checked || []).map((c) => [c.linkId || c.key, c.alive])));
      const dead = (d.checked || []).filter((c) => c.alive === false).length;
      const unknown = (d.checked || []).filter((c) => c.alive == null).length;
      setNotice(
        dead
          ? `${dead} file${dead > 1 ? "s" : ""} dead on its host — copy the magnet, re-upload, and paste the fresh link above.`
          : unknown
            ? "Checked — some could not be verified (API hiccup), try again."
            : "All mapped files are alive. 🟢",
      );
      loadList();
    } catch {
      setNotice("Health check failed — is the host API reachable?");
    } finally {
      setChecking(false);
    }
  };

  const copyMagnet = async (key, magnet) => {
    try {
      await navigator.clipboard.writeText(magnet);
      setCopied(key);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      setNotice("Couldn't copy — select the magnet text manually.");
    }
  };

  const removeLink = async (key, linkId) => {
    const [kind, id, season, episode] = key.split(":");
    const r = await fetch("/api/hosted/link/remove", {
      method: "POST",
      headers: headers(),
      body: JSON.stringify({ type: kind === "tv" ? "episode" : "movie", id: Number(id), season: season ? Number(season) : null, episode: episode ? Number(episode) : null, linkId }),
    });
    if (r.ok) loadList();
  };

  const unmap = async (key) => {
    const [kind, id, season, episode] = key.split(":");
    const r = await fetch("/api/hosted/unmap", {
      method: "POST",
      headers: headers(),
      body: JSON.stringify({ type: kind === "tv" ? "episode" : "movie", id: Number(id), season: season ? Number(season) : null, episode: episode ? Number(episode) : null }),
    });
    if (r.ok) loadList();
  };

  return (
    <div className="admin-card">
      <h2>
        📁 Hosted files — Cloudflare / StreamHG / VidMoly
        {shg?.configured && shg.account && (
          <span className="muted small" style={{ marginLeft: 10, fontWeight: 400 }}>
            ⬡ {shg.account.login}{shg.account.premium ? " · premium" : " · free"}
          </span>
        )}
        {shg?.configured && shg.error && (
          <span className="muted small" style={{ marginLeft: 10, fontWeight: 400 }}>(StreamHG API error: {shg.error})</span>
        )}
        {vm?.configured && vm.account && (
          <span className="muted small" style={{ marginLeft: 10, fontWeight: 400 }}>
            · ▣ {vm.account.login}{vm.account.premium ? " · premium" : " · free"}
          </span>
        )}
        {!shg?.configured && !vm?.configured && <span className="muted small" style={{ marginLeft: 10, fontWeight: 400 }}>(no API keys — health checks off)</span>}
      </h2>
      <p className="muted small">
        One <strong>Add link</strong> button for everything: a film, an episode of a show, or a title nobody has
        catalogued. One link per audio language — a Malayalam original and a Tamil dub are two links on the same
        title, and the player tries them in the order you add them. Both hosts hand the stream straight to PoKu's
        own player (no ads, no extra bandwidth), and a Cloudflare package URL works here too. Hosts delete files
        that go unwatched — re-upload from the saved magnet and paste the fresh link.
      </p>
      <p style={{ marginTop: 4 }}>
        <button className="btn btn-ghost btn-sm" onClick={checkHosts} disabled={checking || !mappings || mappings.length === 0}>
          {checking ? "Checking…" : "🩺 Check files on StreamHG + VidMoly"}
        </button>
      </p>
      {notice && <p className="muted small">{notice}</p>}

      {/* ---- Add link: kind -> title -> single/multi -> language + link ---- */}
      {add ? (
        <div className="admin-add">
          <div className="admin-folders-head">
            <strong>＋ Add link</strong>
            <button className="btn btn-ghost btn-sm" onClick={closeAdd}>
              Close
            </button>
          </div>

          {!add.kind && (
            <div className="admin-choose">
              {[
                { kind: "movie", icon: "🎬", label: "Movies", hint: "A film, by TMDB id" },
                { kind: "tv", icon: "📺", label: "TV Shows", hint: "A show, then a season and episode" },
                { kind: "other", icon: "🗂", label: "Other", hint: "A title nobody has catalogued" },
              ].map((c) => (
                <button key={c.kind} className="admin-choose-btn" onClick={() => startAdd(c.kind)}>
                  <span className="admin-choose-icon">{c.icon}</span>
                  <strong>{c.label}</strong>
                  <span className="muted small">{c.hint}</span>
                </button>
              ))}
            </div>
          )}

          {(add.kind === "movie" || add.kind === "tv") && !add.target && (
            <div className="admin-step">
              <p className="muted small">
                {add.kind === "movie" ? "Which film? Paste its TMDB id, or type the name." : "Which show? Paste its TMDB id, or type the name."}
              </p>
              <div className="admin-yt-picker">
                <input
                  value={addQuery}
                  onChange={(e) => setAddQuery(e.target.value)}
                  placeholder={add.kind === "movie" ? "e.g. 214578 or Interstellar" : "e.g. 1399 or Game of Thrones"}
                  autoFocus
                />
                {addResults.length > 0 && (
                  <div className="admin-yt-results">
                    {addResults.map((r) => (
                      <button key={`${r.manual ? "m" : "t"}${r.id}`} type="button" onClick={() => pickTitle(r)}>
                        {r.manual ? "🗂" : add.kind === "tv" ? "📺" : "🎬"} {r.title} {r.year ? `(${r.year})` : ""}
                        {r.manual ? " · your title" : ""}
                      </button>
                    ))}
                  </div>
                )}
                {addQuery.trim() && addResults.length === 0 && (
                  <p className="muted small">Nothing found yet — keep typing, or use “Other” to name it yourself.</p>
                )}
              </div>
            </div>
          )}

          {add.kind === "other" && !add.target && (
            <div className="admin-step">
              <p className="muted small">
                Nothing has this title, so PoKu will keep it as one of ours — it gets an id of its own and shows up in
                the app like any other film or show.
              </p>
              <div className="admin-episode-fields">
                <input value={addQuery} onChange={(e) => setAddQuery(e.target.value)} placeholder="The name as it should appear — e.g. Big Boss" autoFocus />
                <input value={otherLanguage} onChange={(e) => setOtherLanguage(e.target.value)} placeholder="Language (Malayalam…)" list="hosted-audio-langs" />
              </div>
              <div className="admin-episode-actions">
                <button className="btn btn-primary" onClick={createManualTitle} disabled={addBusy}>
                  {addBusy ? "Creating…" : "Create title"}
                </button>
                <label className="admin-inline-check">
                  <input type="checkbox" checked={add.kind === "other-tv"} onChange={(e) => setAdd((a) => ({ ...a, kind: e.target.checked ? "other-tv" : "other" }))} />
                  it is a show (has seasons)
                </label>
              </div>
            </div>
          )}

          {add.target && (
            <div className="admin-step">
              <p className="admin-breadcrumb">
                {add.kind === "tv" ? "📺" : add.target.manual ? "🗂" : "🎬"} <strong>{add.target.title}</strong>
                {add.target.language ? ` · ${add.target.language}` : ""}
                {add.target.manual ? " · your title" : ""}
              </p>

              {add.kind === "tv" && (
                <div className="admin-episode-fields">
                  <label>
                    Season
                    <input value={add.season} onChange={(e) => setAdd({ ...add, season: e.target.value.replace(/\D/g, "") })} placeholder="8" inputMode="numeric" />
                  </label>
                  <label>
                    Episode
                    <input value={add.episode} onChange={(e) => setAdd({ ...add, episode: e.target.value.replace(/\D/g, "") })} placeholder="1" inputMode="numeric" />
                  </label>
                </div>
              )}

              {!add.mode && (
                <div className="admin-choose">
                  <button
                    className="admin-choose-btn"
                    onClick={() => {
                      setAdd({ ...add, mode: "single" });
                      setAddRows([{ language: add.target.language || "", url: "", magnet: "" }]);
                    }}
                  >
                    <span className="admin-choose-icon">1️⃣</span>
                    <strong>Single</strong>
                    <span className="muted small">
                      One link. Language filled in from {add.target.manual ? "the title" : "TMDB"} — change it if the file is a dub.
                    </span>
                  </button>
                  <button
                    className="admin-choose-btn"
                    onClick={() => {
                      setAdd({ ...add, mode: "multi" });
                      setAddRows([{ language: add.target.language || "", url: "", magnet: "" }]);
                    }}
                  >
                    <span className="admin-choose-icon">🌐</span>
                    <strong>Multi</strong>
                    <span className="muted small">One link per language: type a language, paste, add — then the next one.</span>
                  </button>
                </div>
              )}

              {add.mode === "single" && (
                <AddLinkRow
                  row={addRows[0] || { language: add.target.language || "", url: "", magnet: "" }}
                  onChange={(row) => setAddRows([row])}
                  onAdd={async () => {
                    const okDone = await addLink(addRows[0] || {});
                    if (okDone) setAddRows([{ language: addRows[0]?.language || add.target.language || "", url: "", magnet: "" }]);
                  }}
                  busy={addBusy}
                  submitLabel="Add link"
                />
              )}

              {add.mode === "multi" && (
                <div className="admin-multi">
                  {addRows.map((row, i) => (
                    <AddLinkRow
                      key={i}
                      row={row}
                      onChange={(next) => setAddRows((rows) => rows.map((r, j) => (j === i ? next : r)))}
                      onAdd={async () => {
                        const okDone = await addLink(row);
                        // Keep the language, clear the link, and leave the row
                        // ready for the next language — that is the whole point
                        // of Multi: one pass, many languages.
                        if (okDone) setAddRows((rows) => rows.map((r, j) => (j === i ? { ...r, url: "", magnet: "" } : r)));
                      }}
                      busy={addBusy}
                      submitLabel="Add"
                      showRemove={addRows.length > 1}
                      onRemove={() => setAddRows((rows) => rows.filter((_, j) => j !== i))}
                    />
                  ))}
                  <button className="btn btn-ghost btn-sm" onClick={() => setAddRows((rows) => [...rows, { language: "", url: "", magnet: "" }])}>
                    ＋ Another language
                  </button>
                </div>
              )}
            </div>
          )}

          <datalist id="hosted-audio-langs">
            {Object.values(TMDB_LANGUAGE).map((l) => (
              <option key={l.name} value={l.name} />
            ))}
            <option value="Dual audio" />
          </datalist>
        </div>
      ) : (
        <p className="admin-folders-head">
          <button className="btn btn-primary" onClick={() => startAdd(null)}>
            ＋ Add link
          </button>
          <span className="muted small">A film, an episode, or a title of your own.</span>
        </p>
      )}

      {/* ---- retention: copies that have left the site on their host's clock ---- */}
      <RetentionCard headers={headers} onRenewed={loadList} />

      {/* ---- TV shows: show folder -> season folder -> episode files ---- */}
      <div className="admin-folders">
        <div className="admin-folders-head">
          <strong>📂 TV shows ({tree.shows.length})</strong>
          <button className="btn btn-ghost btn-sm" onClick={() => setShowPicker((p) => !p)}>
            {showPicker ? "Close" : "＋ New folder"}
          </button>
        </div>

        {showPicker && (
          <div className="admin-yt-picker">
            <input
              value={showQuery}
              onChange={(e) => setShowQuery(e.target.value)}
              placeholder="Show name — e.g. Big Boss"
              autoFocus
            />
            {showResults.length > 0 && (
              <div className="admin-yt-results">
                {showResults.map((r) => (
                  <button key={r.id} type="button" onClick={() => createShow(r)}>
                    📁 {r.title} {r.year ? `(${r.year})` : ""} {r.source ? `· ${r.source}` : ""}
                  </button>
                ))}
              </div>
            )}
            {showQuery.trim() && showResults.length === 0 && (
              <p className="muted small">No show found — check the spelling, or add it as a title first.</p>
            )}
          </div>
        )}

        {tree.shows.length === 0 ? (
          <p className="muted small">
            No show folders yet. “＋ New folder” makes one — then a season inside it, then the episodes.
          </p>
        ) : (
          <ul className="admin-folder-list">
            {tree.shows.map((show) => {
              const isOpen = openShow === show.id;
              return (
                <li key={show.id} className="admin-folder">
                  <div className="admin-folder-row">
                    <button
                      className="admin-folder-toggle"
                      onClick={() => {
                        setOpenShow(isOpen ? null : show.id);
                        setOpenSeason(null);
                      }}
                    >
                      <span className="admin-caret">{isOpen ? "▾" : "▸"}</span>
                      <span className="admin-folder-name">📁 {show.title}</span>
                      <span className="muted small">
                        {show.seasonCount} season{show.seasonCount === 1 ? "" : "s"} · {show.episodeCount} episode
                        {show.episodeCount === 1 ? "" : "s"}
                      </span>
                    </button>
                    {isOpen && (
                      <button className="btn btn-ghost btn-sm" onClick={() => setSeasonDraft((d) => (d ? "" : "new"))}>
                        ＋ New folder
                      </button>
                    )}
                  </div>

                  {isOpen && seasonDraft && (
                    <form
                      className="admin-new-folder"
                      onSubmit={(e) => {
                        e.preventDefault();
                        createSeason(show.id, show.title, seasonDraft);
                      }}
                    >
                      <span>📁 New folder in {show.title}:</span>
                      <input
                        value={seasonDraft === "new" ? "" : seasonDraft}
                        onChange={(e) => setSeasonDraft(e.target.value.replace(/\D/g, ""))}
                        placeholder="Season 9"
                        inputMode="numeric"
                        autoFocus
                      />
                      <button className="btn btn-primary btn-sm" type="submit">Create</button>
                      <button className="btn btn-ghost btn-sm" type="button" onClick={() => setSeasonDraft("")}>
                        Cancel
                      </button>
                    </form>
                  )}

                  {isOpen && (
                    <ul className="admin-folder-list admin-folder-seasons">
                      {seasonNumbers(show, blankSeasons[show.id]).map((n) => {
                        const season = show.seasons.find((s) => s.season === n);
                        const isSeasonOpen = openSeason === n;
                        const slots = season
                          ? season.slots
                          : episodeSlots([]).map((s) => ({ ...s, label: episodeLabel(n, s.episode) }));
                        return (
                          <li key={n} className="admin-folder admin-season">
                            <div className="admin-folder-row">
                              <button
                                className="admin-folder-toggle"
                                onClick={() => openSeasonFolder(show.id, n)}
                              >
                                <span className="admin-caret">{isSeasonOpen ? "▾" : "▸"}</span>
                                <span className="admin-folder-name">📁 Season {n}</span>
                                <span className="muted small">
                                  {season ? `${season.count} episode${season.count === 1 ? "" : "s"}` : "empty — add the first one"}
                                </span>
                              </button>
                              {season && (
                                <button
                                  className="btn btn-ghost btn-sm"
                                  onClick={() => addToEpisode(show.id, show.title, n, season.nextEpisode)}
                                >
                                  ＋ Add
                                </button>
                              )}
                            </div>

                            {isSeasonOpen && (
                              <ul className="admin-folder-list admin-episodes">
                                {slots.map((slot) =>
                                  slot.mapping ? (
                                    <li key={slot.episode} className={`admin-episode${(slot.mapping.links || []).length === 0 ? " dead" : ""}`}>
                                      <div className="admin-episode-row">
                                        <button
                                          className="admin-episode-label"
                                          title="Add another link to this episode"
                                          onClick={() => addToEpisode(show.id, show.title, n, slot.episode)}
                                        >
                                          {slot.label}
                                        </button>
                                        <span className="muted small">
                                          {(slot.mapping.links || []).length} link{(slot.mapping.links || []).length === 1 ? "" : "s"}
                                        </span>
                                        <button className="btn btn-ghost btn-sm" onClick={() => unmap(slot.mapping.key)}>
                                          Remove all
                                        </button>
                                        <button
                                          className="btn btn-ghost btn-sm"
                                          title="Add a subtitle for this episode"
                                          onClick={() => openSubForm(slot.mapping.key, show.title, "episode")}
                                        >
                                          💬 Subtitle
                                        </button>
                                      </div>
                                      {linkRows(slot.mapping)}
                                      <SubtitleTracks node={slot.mapping} headers={headers} onChanged={loadSubs} />
                                      {subTarget?.key === slot.mapping.key && (
                                        <SubtitleForm target={subTarget} headers={headers} onChanged={loadSubs} />
                                      )}
                                    </li>
                                  ) : (
                                    <li key={slot.episode} className="admin-episode empty">
                                      <button
                                        className="admin-episode-empty"
                                        onClick={() => addToEpisode(show.id, show.title, n, slot.episode)}
                                      >
                                        ＋ {slot.label} — empty
                                      </button>
                                    </li>
                                  ),
                                )}
                              </ul>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* ---- Movies: a flat list, no seasons to fold into ---- */}
      <div className="admin-folders">
        <div className="admin-folders-head">
          <strong>🎬 Movies ({tree.movies.length})</strong>
        </div>
        {tree.movies.length > 0 && (
          <ul className="admin-livetv-list">
            {tree.movies.map((m) => (
              <li key={m.key} className={(m.links || []).length === 0 ? "dead" : ""}>
                <span className="admin-livetv-name">🎞 {m.title || m.key}</span>
                <span className="admin-livetv-url">{(m.links || []).length} link{(m.links || []).length === 1 ? "" : "s"}</span>
                <button className="btn btn-ghost btn-sm" onClick={() => unmap(m.key)}>Remove all</button>
                <button className="btn btn-ghost btn-sm" title="Add a subtitle for this film" onClick={() => openSubForm(m.key, m.title, "movie")}>
                  💬 Subtitle
                </button>
                {linkRows(m)}
                <SubtitleTracks node={m} headers={headers} onChanged={loadSubs} />
                {subTarget?.key === m.key && <SubtitleForm target={subTarget} headers={headers} onChanged={loadSubs} />}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// --- subtitles --------------------------------------------------------------

// Upload SRT/VTT subtitle files for a title. Hosted-file sources (and torrent
// streams) carry video only — subs baked into the release never survive the
// host's transcode — so PoKu stores the tracks itself and attaches them as
// HTML5 <track> elements with a CC language picker in the player.
// --- track extraction -------------------------------------------------------

// Pull every audio track and subtitle out of a movie into one multi-audio HLS
// (video copied, one AAC rendition per track) so the player gets a real audio
// switcher, with the subtitles embedded in the file converted to WebVTT. The
// source is a direct link (Seedr, Drive, any host) or a file on this machine.
// ffmpeg has to run here, so this works on the local server only.
function TrackExtractorCard({ headers }) {
  const [form, setForm] = useState({ query: "", input: "", defaultIndex: 0, subtitles: true });
  const [picked, setPicked] = useState(null);
  const [results, setResults] = useState([]);
  const [probe, setProbe] = useState(null);
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(false);
  const [job, setJob] = useState(null);
  const [unsupported, setUnsupported] = useState(false);

  // Same title picker as the other cards: name search or a raw TMDB id.
  useEffect(() => {
    const q = form.query.trim();
    if (!q || picked) return undefined;
    const numeric = Number.isInteger(Number(q)) && q.length >= 2;
    const t = setTimeout(async () => {
      try {
        if (numeric) {
          const d = await tmdb.movieDetails(Number(q)).catch(() => null);
          setResults(d ? [{ tmdbId: d.tmdbId, title: d.title, year: (d.releaseDate || "").slice(0, 4) }] : []);
        } else {
          const page = await tmdb.searchMulti(q, 1);
          setResults(
            toCards(page.results)
              .filter((c) => c.contentType === ContentType.MOVIE)
              .slice(0, 6)
              .map((c) => ({ tmdbId: c.tmdbId, title: c.title, year: (c.date || "").slice(0, 4) })),
          );
        }
      } catch {
        setResults([]);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [form.query, picked]);

  // Poll the running job until it settles.
  useEffect(() => {
    if (!job || job.status !== "running") return undefined;
    const iv = setInterval(async () => {
      try {
        const r = await fetch(`/api/tracks/status?jobId=${encodeURIComponent(job.id)}`, { headers: headers() });
        if (!r.ok) return;
        setJob(await r.json());
      } catch {
        /* polling is best-effort */
      }
    }, 2000);
    return () => clearInterval(iv);
  }, [job, headers]);

  const post = async (path, body) => {
    const r = await fetch(path, { method: "POST", headers: headers(), body: JSON.stringify(body) });
    // 404 on the deploy, 501 when the host has no ffmpeg.
    if (r.status === 404 || r.status === 501) {
      setUnsupported(true);
      throw new Error("unavailable");
    }
    const d = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
    return d;
  };

  const onProbe = async () => {
    setNotice(null);
    setProbe(null);
    if (!form.input.trim()) {
      setNotice("Paste a direct video link or a file path first.");
      return;
    }
    setBusy(true);
    try {
      const d = await post("/api/tracks/probe", { input: form.input.trim(), id: picked ? picked.tmdbId : 0 });
      setProbe(d.probe);
      setForm((f) => ({ ...f, defaultIndex: d.probe && d.probe.audio && d.probe.audio[0] ? d.probe.audio[0].index : 0 }));
    } catch (e) {
      if (e.message !== "unavailable") setNotice(e.message);
    } finally {
      setBusy(false);
    }
  };

  const onExtract = async () => {
    setNotice(null);
    setJob(null);
    if (!picked) {
      setNotice("Pick a title first — the result is published under that TMDB id.");
      return;
    }
    if (!form.input.trim()) {
      setNotice("Paste a direct video link or a file path first.");
      return;
    }
    setBusy(true);
    try {
      const d = await post("/api/tracks/extract", {
        input: form.input.trim(),
        id: picked.tmdbId,
        title: picked.title,
        defaultIndex: Number(form.defaultIndex) || 0,
        subtitles: form.subtitles,
      });
      setJob({ id: d.jobId, status: "running", progress: { stage: "queued" } });
      setNotice("Extraction started — keep this page open; it updates as it goes.");
    } catch (e) {
      if (e.message !== "unavailable") setNotice(e.message);
    } finally {
      setBusy(false);
    }
  };

  const p = (job && job.progress) || {};
  const stageLabel =
    {
      queued: "Queued…",
      probing: "Reading the source…",
      probed: "Source read",
      remuxing: `Writing HLS${p.segments ? ` — ${p.segments} segments` : ""}…`,
      remuxed: "HLS written",
      subtitles: `Extracting subtitle: ${p.track || ""}`,
      "subtitles-done": "Subtitles done",
      done: "Finished",
      failed: "Failed",
    }[p.stage] || p.stage;

  return (
    <div className="admin-card">
      <h2>🎚 Audio &amp; subtitle extraction</h2>
      <p className="muted small">
        Point this at a movie — a direct link (Seedr, Drive, any host) or a file already on this machine — and it pulls
        <strong> every audio track</strong> into a single HLS stream the player can switch between, and converts the
        subtitles embedded in the file into WebVTT. Video is copied, never re-encoded, so it is fast and lossless, and a
        link is read over HTTP so it never has to be downloaded first. Needs ffmpeg, so it runs on the local server only.
      </p>
      <form
        className="admin-yt-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (probe) onExtract();
          else onProbe();
        }}
      >
        <div className="admin-yt-picker">
          <input
            value={picked ? `${picked.title} (${picked.year || ""})` : form.query}
            onChange={(e) => {
              setPicked(null);
              setForm({ ...form, query: e.target.value });
            }}
            placeholder="Movie title or TMDB id…"
          />
          {!picked && results.length > 0 && (
            <div className="admin-yt-results">
              {results.map((r) => (
                <button key={r.tmdbId} type="button" onClick={() => setPicked(r)}>
                  {r.title} {r.year ? `(${r.year})` : ""}
                </button>
              ))}
            </div>
          )}
        </div>
        <input
          value={form.input}
          onChange={(e) => setForm({ ...form, input: e.target.value })}
          placeholder="https://…/movie.mkv   or   /path/to/movie.mkv *"
        />
        <button className="btn btn-ghost btn-sm" type="submit" disabled={busy}>
          {busy ? "Working…" : probe ? "Extract tracks" : "Read tracks"}
        </button>
        {probe && (
          <button
            className="btn btn-ghost btn-sm"
            type="button"
            onClick={() => {
              setProbe(null);
              setNotice(null);
            }}
          >
            Reset
          </button>
        )}
      </form>
      {unsupported && (
        <p className="source-check bad">
          Track extraction needs ffmpeg, which only runs on the local server. Start it locally and open the admin from
          there.
        </p>
      )}
      {probe && (
        <div className="muted small" style={{ marginTop: 8 }}>
          <p>
            <strong>{probe.audio.length}</strong> audio track(s)
            {probe.durationSec ? ` · ${Math.round(probe.durationSec / 60)} min` : ""} · default audio:
          </p>
          <select
            value={form.defaultIndex}
            onChange={(e) => setForm({ ...form, defaultIndex: Number(e.target.value) })}
            style={{ marginBottom: 6 }}
          >
            {probe.audio.map((t) => (
              <option key={t.index} value={t.index}>
                {t.label}
                {t.code ? ` (${t.code})` : ""}
              </option>
            ))}
          </select>
          <ul className="admin-livetv-list">
            {probe.audio.map((t) => (
              <li key={`a${t.index}`}>
                <span className="admin-livetv-name">🎙 {t.label}</span>
                <span className="admin-livetv-url">{t.codec || "?"}</span>
              </li>
            ))}
            {probe.subtitles.map((t) => (
              <li key={`s${t.index}`}>
                <span className="admin-livetv-name">💬 {t.label}</span>
                <span className="admin-livetv-url">
                  {t.kind === "image" ? "image-based — cannot convert" : t.codec || "?"}
                </span>
              </li>
            ))}
          </ul>
          <label style={{ display: "block", marginTop: 6 }}>
            <input
              type="checkbox"
              checked={form.subtitles}
              onChange={(e) => setForm({ ...form, subtitles: e.target.checked })}
            />{" "}
            Extract subtitles too
          </label>
        </div>
      )}
      {job && (
        <div className="muted small" style={{ marginTop: 8 }}>
          <p>
            <strong>
              {job.status === "done" ? "✅" : job.status === "failed" ? "🔴" : "⏳"} {stageLabel}
            </strong>
          </p>
          {job.status === "failed" && <p className="source-check bad">{job.error}</p>}
          {job.status === "done" && job.result && (
            <p>
              Playable now at <code>{job.result.hlsUrl}</code> — open the title and use the player's audio menu. Audio:{" "}
              {job.result.audio.map((a) => a.label).join(", ")}.
              {job.result.subtitlesSaved.length > 0 ? ` Subtitles: ${job.result.subtitlesSaved.join(", ")}.` : ""}
              {job.result.subtitlesSkipped.length > 0
                ? ` Skipped: ${job.result.subtitlesSkipped.map((s) => `${s.label} (${s.reason})`).join(", ")}.`
                : ""}
            </p>
          )}
        </div>
      )}
      {notice && <p className="muted small">{notice}</p>}
    </div>
  );
}

function SubtitlesCard({ headers }) {
  const [mappings, setMappings] = useState(null);
  const [form, setForm] = useState({ query: "", lang: "", fileText: null, fileName: "", importUrl: "" });
  const [picked, setPicked] = useState(null);
  const [results, setResults] = useState([]);
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(false);
  // Online finder (Podnapisi): search results + the entry being attached.
  const [online, setOnline] = useState({ items: null, searching: false, attaching: null, error: null });

  const loadList = useCallback(() => {
    fetch("/api/subs/list")
      .then((r) => r.json())
      .then((d) => setMappings(d.mappings || []))
      .catch(() => setMappings([]));
  }, []);
  useEffect(loadList, [loadList]);

  // Same picker as the hosted-files card: name search or raw TMDB id.
  useEffect(() => {
    const q = form.query.trim();
    if (!q || picked) return undefined;
    const t = setTimeout(async () => {
      try {
        if (/^\d+$/.test(q) && q.length >= 2) {
          const d = await tmdb.movieDetails(Number(q)).catch(() => null);
          setResults(d ? [{ tmdbId: d.tmdbId, title: d.title, year: (d.releaseDate || "").slice(0, 4) }] : []);
        } else {
          const page = await tmdb.searchMulti(q, 1);
          setResults(
            toCards(page.results)
              .filter((c) => c.contentType === ContentType.MOVIE)
              .slice(0, 6)
              .map((c) => ({ tmdbId: c.tmdbId, title: c.title, year: (c.date || "").slice(0, 4) })),
          );
        }
      } catch {
        setResults([]);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [form.query, picked]);

  const readSrt = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, fileText: String(reader.result || ""), fileName: file.name }));
    reader.readAsText(file);
  };

  const add = async (e) => {
    e.preventDefault();
    setNotice(null);
    if (!picked) {
      setNotice("Pick a title from the search results first.");
      return;
    }
    if (!form.fileText && !form.importUrl.trim()) {
      setNotice("Choose an .srt file or paste a subtitle URL.");
      return;
    }
    setBusy(true);
    try {
      const r = await fetch("/api/subs/add", {
        method: "POST",
        headers: headers(),
        body: JSON.stringify({
          type: "movie",
          id: picked.tmdbId,
          lang: form.lang || "English",
          title: picked.title,
          content: form.fileText,
          importUrl: form.fileText ? null : form.importUrl.trim(),
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        setNotice(d.error || "Could not add that subtitle");
        return;
      }
      setForm({ query: "", lang: "", fileText: null, fileName: "", importUrl: "" });
      setPicked(null);
      setResults([]);
      setNotice("Subtitle added — it now shows as a CC option whenever this title plays from PoKu's own player.");
      loadList();
    } finally {
      setBusy(false);
    }
  };

  // Search SubDL for the picked title (by TMDB id) and list downloadable files.
  const findOnline = async () => {
    if (!picked) {
      setNotice("Pick a title from the search results first.");
      return;
    }
    setOnline({ items: null, searching: true, attaching: null, error: null });
    try {
      const qs = new URLSearchParams({ type: "movie", id: picked.tmdbId, lang: (form.lang || "english").toLowerCase() });
      const r = await fetch(`/api/subs/podnapisi/search?${qs}`, { headers: headers() });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || "Search failed");
      setOnline({ items: d.results || [], searching: false, attaching: null, error: (d.results || []).length ? null : "No subtitles found for this language — try another language or a manual upload." });
    } catch (e) {
      setOnline({ items: [], searching: false, attaching: null, error: e.message });
    }
  };

  // Attach a SubDL result: server downloads the ZIP, extracts the .srt,
  // converts to VTT and stores it — same end state as a manual upload.
  const attachOnline = async (item) => {
    if (!picked) return;
    setOnline((o) => ({ ...o, attaching: item.id, error: null }));
    try {
      const r = await fetch("/api/subs/podnapisi/add", {
        method: "POST",
        headers: headers(),
        body: JSON.stringify({
          subdlUrl: item.url,
          type: "movie",
          id: picked.tmdbId,
          lang: form.lang || "English",
          title: picked.title,
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || "Could not attach that subtitle");
      setOnline({ items: null, searching: false, attaching: null, error: null });
      setNotice(`Attached "${item.name}" to ${picked.title} — it now plays as a CC track.`);
      loadList();
    } catch (e) {
      setOnline((o) => ({ ...o, attaching: null, error: e.message }));
    }
  };

  const removeTrack = async (key, trackId) => {
    const [kind, id, season, episode] = key.split(":");
    const r = await fetch("/api/subs/remove", {
      method: "POST",
      headers: headers(),
      body: JSON.stringify({ type: kind === "tv" ? "episode" : "movie", id: Number(id), season: season ? Number(season) : null, episode: episode ? Number(episode) : null, trackId }),
    });
    if (r.ok) loadList();
  };

  const unmap = async (key) => {
    const [kind, id, season, episode] = key.split(":");
    const r = await fetch("/api/subs/unmap", {
      method: "POST",
      headers: headers(),
      body: JSON.stringify({ type: kind === "tv" ? "episode" : "movie", id: Number(id), season: season ? Number(season) : null, episode: episode ? Number(episode) : null }),
    });
    if (r.ok) loadList();
  };

  return (
    <div className="admin-card">
      <h2>💬 Subtitles</h2>
      <p className="muted small">
        Upload .srt (or .vtt) files per title — they play as CC tracks on every native source (hosted file, torrent).
        Or press <strong>Find online</strong> to pull a ready-made subtitle from SubDL's public database, matched by TMDB
        id (works for torrent "ESub" releases — the subs there are embedded in the MKV, so this is how to get them as a
        real file). Free SubDL API key required (set <code>SUBDL_API_KEY</code>).
        One track per language; re-adding replaces.
      </p>
      <form className="admin-yt-form" onSubmit={add}>
        <div className="admin-yt-picker">
          <input
            value={picked ? `${picked.title} (${picked.year || ""})` : form.query}
            onChange={(e) => {
              setPicked(null);
              setForm({ ...form, query: e.target.value });
            }}
            placeholder="Movie title or TMDB id…"
            required
          />
          {!picked && results.length > 0 && (
            <div className="admin-yt-results">
              {results.map((r) => (
                <button key={r.tmdbId} type="button" onClick={() => setPicked(r)}>
                  {r.title} {r.year ? `(${r.year})` : ""}
                </button>
              ))}
            </div>
          )}
        </div>
        <input value={form.lang} onChange={(e) => setForm({ ...form, lang: e.target.value })} placeholder="Language (English, Malayalam, Tamil…)" list="subs-langs" />
        <datalist id="subs-langs">
          <option value="English" />
          <option value="Malayalam" />
          <option value="Tamil" />
          <option value="Telugu" />
          <option value="Hindi" />
          <option value="Kannada" />
        </datalist>
        <label className="admin-subs-file">
          <input type="file" accept=".srt,.vtt,text/plain" onChange={(e) => readSrt(e.target.files?.[0])} />
          {form.fileName ? `📄 ${form.fileName}` : "Choose .srt file…"}
        </label>
        <input value={form.importUrl} onChange={(e) => setForm({ ...form, importUrl: e.target.value, fileText: null, fileName: "" })} placeholder="…or paste a direct .srt URL (e.g. from Seedr)" />
        <div className="admin-subs-actions">
          <button className="btn btn-primary" type="submit" disabled={busy}>
            {busy ? "Adding…" : "Add subtitle"}
          </button>
          <button className="btn btn-ghost" type="button" onClick={findOnline} disabled={online.searching || busy}>
            {online.searching ? "Searching…" : "🌐 Find online (SubDL)"}
          </button>
        </div>
      </form>
      {online.error && <p className="muted small">{online.error}</p>}
      {online.items && online.items.length > 0 && (
        <ul className="admin-livetv-list">
          {online.items.map((item) => (
            <li key={item.id}>
              <span className="admin-livetv-name">🌐 {item.releaseName || item.name}</span>
              <span className="admin-livetv-url">{item.language}{item.hi ? " · HI" : ""}</span>
              <button className="btn btn-ghost btn-sm" onClick={() => attachOnline(item)} disabled={online.attaching != null}>
                {online.attaching === item.id ? "Attaching…" : "Attach"}
              </button>
            </li>
          ))}
        </ul>
      )}
      {notice && <p className="muted small">{notice}</p>}
      {mappings && mappings.length > 0 && (
        <ul className="admin-livetv-list">
          {mappings.map((m) => (
            <li key={m.key}>
              <span className="admin-livetv-name">💬 {m.title || m.key}</span>
              <span className="admin-livetv-url">{(m.tracks || []).length} track{(m.tracks || []).length === 1 ? "" : "s"}</span>
              <button className="btn btn-ghost btn-sm" onClick={() => unmap(m.key)}>Remove all</button>
              <ul className="admin-hosted-links">
                {(m.tracks || []).map((t) => (
                  <li key={t.id}>
                    <span className="admin-hosted-audio" title="Subtitle language">💬 {t.label}</span>
                    <span className="admin-livetv-url">{t.file}</span>
                    <button className="btn btn-ghost btn-sm" onClick={() => removeTrack(m.key, t.id)}>Remove</button>
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// --- title picker + add form -------------------------------------------------------

function MappingForm({ headers, onAdded }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [picked, setPicked] = useState(null); // { tmdbId, contentType, title, posterPath, date }
  const [showResults, setShowResults] = useState(false);
  const [form, setForm] = useState({ magnet: "", name: "", season: "", episode: "" });
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const boxRef = useRef(null);

  // Search-as-you-type (debounced). Also accepts a pasted raw TMDB id: digits
  // resolve straight to that movie/tv title for confirmation.
  useEffect(() => {
    const q = query.trim();
    if (!q || picked) return undefined;
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        if (/^\d+$/.test(q) && q.length >= 2) {
          const found = await lookupById(q);
          setResults(found ? [found] : []);
        } else {
          const page = await tmdb.searchMulti(q, 1);
          setResults(
            toCards(page.results)
              .filter((c) => c.contentType !== ContentType.EPISODE)
              .slice(0, 8),
          );
        }
        setShowResults(true);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query, picked]);

  // Close the dropdown on outside clicks.
  useEffect(() => {
    const onDoc = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setShowResults(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const isEpisode = picked?.contentType === ContentType.EPISODE;

  const submit = async (e) => {
    e.preventDefault();
    if (!picked) return;
    setBusy(true);
    setNotice(null);
    try {
      const body = {
        type: isEpisode ? "episode" : "movie",
        id: picked.tmdbId,
        magnet: form.magnet.trim(),
        name: form.name || null,
        ...(isEpisode ? { season: form.season, episode: form.episode } : {}),
      };
      const r = await fetch("/api/torrent/add", { method: "POST", headers: headers(), body: JSON.stringify(body) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || "Failed");
      setNotice(`Mapped “${picked.title}”. First play warms the swarm; the HLS remux starts after the download.`);
      setPicked(null);
      setQuery("");
      setForm({ magnet: "", name: "", season: "", episode: "" });
      onAdded();
    } catch (err) {
      setNotice(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="admin-form" onSubmit={submit}>
      {!picked ? (
        <label className="admin-search" ref={boxRef}>
          Find title (name or TMDB id)
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => results.length > 0 && setShowResults(true)}
            placeholder='e.g. "Gandhari" or 1479832'
            autoComplete="off"
            required
          />
          {searching && <span className="muted small">searching…</span>}
          {showResults && results.length > 0 && (
            <div className="admin-results">
              {results.map((c) => (
                <button
                  type="button"
                  key={`${c.contentType}-${c.tmdbId}`}
                  className="admin-result"
                  onClick={() => {
                    setPicked(c);
                    setShowResults(false);
                  }}
                >
                  <Poster posterPath={c.posterPath} title={c.title} />
                  <span className="admin-result-text">
                    <strong>{c.title}</strong>
                    <span className="muted small">
                      {c.contentType === ContentType.MOVIE ? "Movie" : "TV"} · {yearOf(c.date) || "—"} · ★{" "}
                      {Number(c.rating || 0).toFixed(1)} · id {c.tmdbId}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          )}
          {showResults && !searching && results.length === 0 && query.trim() && (
            <div className="admin-results">
              <p className="muted small" style={{ padding: "8px 10px", margin: 0 }}>
                No matches. A numeric id with no match means the id is wrong or the title is unreleased.
              </p>
            </div>
          )}
        </label>
      ) : (
        <div className="admin-picked">
          <Poster posterPath={picked.posterPath} title={picked.title} big />
          <div className="admin-picked-text">
            <strong>{picked.title}</strong>
            <span className="muted small">
              {picked.contentType === ContentType.MOVIE ? "Movie" : "TV"} · {yearOf(picked.date) || "—"} · id{" "}
              {picked.tmdbId}
            </span>
            <button type="button" className="btn btn-ghost" onClick={() => setPicked(null)}>
              Change title
            </button>
          </div>
        </div>
      )}

      {isEpisode && (
        <div className="admin-grid">
          <label>
            Season
            <input value={form.season} onChange={(e) => setForm((f) => ({ ...f, season: e.target.value.replace(/\D/g, "") }))} placeholder="1" required />
          </label>
          <label>
            Episode
            <input value={form.episode} onChange={(e) => setForm((f) => ({ ...f, episode: e.target.value.replace(/\D/g, "") }))} placeholder="1" required />
          </label>
        </div>
      )}

      <label>
        Label (optional)
        <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="1080p BluRay dual-audio" />
      </label>

      <label>
        Magnet link
        <textarea
          value={form.magnet}
          onChange={(e) => setForm((f) => ({ ...f, magnet: e.target.value }))}
          placeholder="magnet:?xt=urn:btih:…  (or a https .torrent URL)"
          rows={3}
          required
        />
      </label>

      <div className="player-actions">
        <button className="btn btn-primary" type="submit" disabled={busy || !picked}>
          {busy ? "Saving…" : "Add mapping"}
        </button>
      </div>
      {notice && <p className="source-check warn">{notice}</p>}
    </form>
  );
}

// Raw-id lookup used by both the picker and pasted ids. Searches TMDB's find
// fallback: movie first, then tv.
async function lookupById(id) {
  for (const [fn, type] of [
    [tmdb.movieDetails, ContentType.MOVIE],
    [tmdb.tvDetails, ContentType.TV_SHOW],
  ]) {
    try {
      const d = await fn(id);
      if (d && (d.title || d.name)) {
        return {
          tmdbId: d.id,
          contentType: type,
          title: d.title || d.name,
          posterPath: d.poster_path || null,
          date: d.release_date || d.first_air_date || null,
        };
      }
    } catch {
      /* try next type */
    }
  }
  return null;
}

function Poster({ posterPath, title, big = false }) {
  const size = big ? "w92" : "w45";
  if (posterPath) {
    return <img className="admin-poster" src={`https://image.tmdb.org/t/p/${size}${posterPath}`} alt="" loading="lazy" />;
  }
  return <span className={`admin-poster admin-poster-empty${big ? " big" : ""}`}>{(title || "?").slice(0, 1)}</span>;
}

// --- mappings list + track editor ---------------------------------------------------

function MappingList({ entries, headers, onChanged }) {
  const [editing, setEditing] = useState(null); // entry.key while editing tracks
  if (entries.length === 0) {
    return (
      <>
        <h2 className="row-title">Current mappings (0)</h2>
        <p className="muted">No torrents mapped yet.</p>
      </>
    );
  }
  return (
    <>
      <h2 className="row-title">Current mappings ({entries.length})</h2>
      <div className="admin-list">
        {entries.map((e) => (
          <div key={e.key} className="admin-entry">
            <div>
              <strong>{e.name || e.key}</strong>
              <div className="muted small">
                {e.key} · {(e.status?.progress ?? 0) >= 0.999 ? "downloaded ✓" : "downloading"} ·{" "}
                {e.status?.length ? `${(e.status.length / 1_000_000_000).toFixed(2)} GB` : "size unknown"} ·{" "}
                {e.status ? `${Math.round((e.status.progress || 0) * 100)}% · ${e.status.numPeers || 0} peers` : ""}
              </div>
              <div className="muted small">
                HLS:{" "}
                {e.status?.hls?.status === "ready"
                  ? `ready — ${(e.status.hls.tracks || []).map((t) => t.name || t.code || `a${t.index}`).join(", ") || "no audio tracks"}`
                  : e.status?.hls?.status === "running"
                    ? "remuxing…"
                    : e.status?.hls?.status === "failed"
                      ? `failed — ${e.status.hls.error || "unknown error"}`
                      : e.status?.hls?.status === "queued"
                        ? "queued (waiting for download)"
                        : "not started (starts after download)"}
              </div>
              <div className="muted small trunc">{e.magnet}</div>
              {e.status?.torbox && (
                <div className="muted small">
                  TorBox: {e.status.torbox.cached ? "cached ⚡" : e.status.torbox.progress != null ? `cloud ${Math.round(e.status.torbox.progress * 100)}%` : "queued"}
                </div>
              )}
            </div>
            <div className="admin-entry-actions">
              {e.status?.hls?.status === "ready" && (
                <button className="btn btn-ghost" onClick={() => setEditing(editing === e.key ? null : e.key)}>
                  {editing === e.key ? "Close editor" : "Languages"}
                </button>
              )}
              {(e.status?.hls?.status === "failed" || e.status?.hls?.status === "ready") && (
                <button
                  className="btn btn-ghost"
                  onClick={async () => {
                    await fetch("/api/torrent/remux", {
                      method: "POST",
                      headers: headers(),
                      body: JSON.stringify(keyToBody(e.key)),
                    });
                    onChanged();
                  }}
                >
                  Re-run remux
                </button>
              )}
              <button
                className="btn btn-ghost"
                onClick={async () => {
                  await fetch("/api/torrent/remove", {
                    method: "POST",
                    headers: headers(),
                    body: JSON.stringify(keyToBody(e.key)),
                  });
                  onChanged();
                }}
              >
                Remove
              </button>
            </div>
          </div>
        ))}
      </div>
      {editing &&
        entries
          .filter((e) => e.key === editing)
          .map((e) => <TrackEditor key={e.key} entry={e} headers={headers} onSaved={onChanged} />)}
    </>
  );
}

function TrackEditor({ entry, headers, onSaved }) {
  const [tracks, setTracks] = useState(
    (entry.status?.hls?.tracks || []).map((t) => ({ index: t.index, code: t.code || "", name: t.name || "" })),
  );
  const [defaultIndex, setDefaultIndex] = useState(entry.status?.hls?.defaultIndex ?? 0);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);

  const save = async () => {
    setBusy(true);
    setNotice(null);
    try {
      const r = await fetch("/api/torrent/tracks", {
        method: "POST",
        headers: headers(),
        body: JSON.stringify({ ...keyToBody(entry.key), tracks, defaultIndex }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || "Failed");
      setNotice("Saved — master playlist rewritten.");
      onSaved();
    } catch (err) {
      setNotice(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="admin-track-editor">
      <h3>Languages — {entry.name || entry.key}</h3>
      <p className="muted small">
        Name each audio track the way viewers will see it (e.g. “Hindi”, “Tamil”) and pick the default language. These
        are playlist edits — nothing is re-encoded.
      </p>
      {tracks.length === 0 ? (
        <p className="muted small">No audio tracks detected in this file.</p>
      ) : (
        <table className="admin-tracks">
          <thead>
            <tr>
              <th>Track</th>
              <th>Shown name</th>
              <th>Code (hin/tam/…)</th>
              <th>Default</th>
            </tr>
          </thead>
          <tbody>
            {tracks.map((t) => (
              <tr key={t.index}>
                <td>Audio {t.index + 1}</td>
                <td>
                  <input
                    value={t.name}
                    onChange={(e) =>
                      setTracks((ts) => ts.map((x) => (x.index === t.index ? { ...x, name: e.target.value } : x)))
                    }
                    placeholder={`Audio ${t.index + 1}`}
                  />
                </td>
                <td>
                  <input
                    value={t.code}
                    onChange={(e) =>
                      setTracks((ts) => ts.map((x) => (x.index === t.index ? { ...x, code: e.target.value } : x)))
                    }
                    placeholder="hin"
                    size={6}
                  />
                </td>
                <td>
                  <input
                    type="radio"
                    name={`default-${entry.key}`}
                    checked={defaultIndex === t.index}
                    onChange={() => setDefaultIndex(t.index)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className="player-actions">
        <button className="btn btn-primary" onClick={save} disabled={busy}>
          {busy ? "Saving…" : "Save languages"}
        </button>
      </div>
      {notice && <p className="source-check warn">{notice}</p>}
    </div>
  );
}

// "movie:155" | "tv:99:1:2" -> { type, id, season?, episode? }
function keyToBody(key) {
  const [type, id, season, episode] = key.split(":");
  return type === "episode"
    ? { type: "episode", id, season, episode }
    : { type: "movie", id };
}
